/**
 * PwaToast — slides in from the bottom-right with a message + an
 * optional action button. Used by the PWA layer to surface two
 * events that otherwise live silently in the console:
 *
 *   - "New version available, reload?" — service worker has a fresh
 *     bundle waiting; clicking Reload calls the registerSW
 *     update() function which swaps the active SW.
 *   - "App ready offline" — first install completed, app will work
 *     without a network connection from now on.
 *
 * Dismissable via an inline ✕. Self-contained — main.ts hands it
 * messages and disposes when the page tears down (which never
 * happens in practice, but the API stays consistent).
 */

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastOptions {
  readonly message: string;
  readonly action?: ToastAction;
  /** Auto-dismiss after this many ms. 0 = sticky. Default 8000. */
  readonly autoDismissMs?: number;
}

/**
 * Singleton toast host. Mount it once near the end of <body> and the
 * `show()` method displays one toast at a time — replaying replaces
 * whatever's currently on screen so the user never sees a stack.
 */
export class PwaToast {
  readonly element: HTMLElement;
  private currentTimer: ReturnType<typeof setTimeout> | null = null;
  private currentToast: HTMLElement | null = null;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'pwa-toast-host';
    this.element.setAttribute('role', 'status');
    this.element.setAttribute('aria-live', 'polite');
  }

  /**
   * Replace the current toast (if any) with a new one. Auto-dismisses
   * after `autoDismissMs` unless an action is supplied — sticky on
   * actionable toasts so the user has time to read + click.
   */
  show(opts: ToastOptions): void {
    this.dismiss();

    const toast = document.createElement('div');
    toast.className = 'pwa-toast';

    const msg = document.createElement('span');
    msg.className = 'pwa-toast-message';
    msg.textContent = opts.message;
    toast.appendChild(msg);

    if (opts.action) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm pwa-toast-action';
      btn.textContent = opts.action.label;
      btn.addEventListener('click', () => {
        opts.action?.onClick();
        this.dismiss();
      });
      toast.appendChild(btn);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'pwa-toast-close';
    close.textContent = '✕';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.addEventListener('click', () => this.dismiss());
    toast.appendChild(close);

    this.element.appendChild(toast);
    this.currentToast = toast;

    // Default 8s auto-dismiss for non-actionable; sticky if action
    // present so the user has time to read and click.
    const auto = opts.autoDismissMs ?? (opts.action ? 0 : 8000);
    if (auto > 0) {
      this.currentTimer = setTimeout(() => this.dismiss(), auto);
    }
  }

  dismiss(): void {
    if (this.currentTimer) {
      clearTimeout(this.currentTimer);
      this.currentTimer = null;
    }
    if (this.currentToast) {
      this.currentToast.remove();
      this.currentToast = null;
    }
  }
}
